'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

const NAV_LINKS = [
  { href: '/#features', label: 'Features' },
  { href: '/#pricing', label: 'Pricing' },
  { href: '/ifta/', label: 'IFTA by State' },
  { href: '/faq/', label: 'FAQ' },
  { href: '/blog/', label: 'Blog' },
  { href: 'https://app.ifueltax.com/#/login', label: 'Login', external: true },
];

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
}

export default function Header() {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      transition: 'box-shadow 0.3s',
      background: 'rgba(255,255,255,0.97)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(15,23,42,0.08)',
      boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : '0 1px 0 rgba(15,23,42,0.06)',
    }}>
      <div style={{
        maxWidth: 1200, margin: '0 auto', padding: '0 24px',
        display: 'flex', alignItems: 'center', height: 68,
      }}>
        {/* Logo */}
        <Link href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img
            src="/images/ifueltax-logo.png"
            alt="iFuelTax"
            width={128}
            height={40}
            style={{ height: 40, width: 'auto', display: 'block' }}
          />
        </Link>

        {/* Desktop nav */}
        <div style={{
          marginLeft: 40, display: 'flex', gap: 6,
          '@media (max-width: 767px)': { display: 'none' },
        }} className="hdr-desktop-nav">
          {NAV_LINKS.map(({ href, label, external }) => {
            const linkStyle = {
              color: 'rgba(15,23,42,0.6)',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500,
              padding: '6px 14px',
              borderRadius: 6,
              transition: 'color 0.2s',
            };
            const hoverIn = e => e.currentTarget.style.color = '#0F172A';
            const hoverOut = e => e.currentTarget.style.color = 'rgba(15,23,42,0.6)';
            return external ? (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                {label}
              </a>
            ) : (
              <Link key={href} href={href} style={linkStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                {label}
              </Link>
            );
          })}
        </div>

        {/* CTA buttons */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}
          className="hdr-desktop-nav">
          <a
            href="https://app.ifueltax.com/#/login?action_type=register"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center',
              background: '#3B82F6',
              color: '#fff',
              padding: '9px 20px',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 700,
              textDecoration: 'none',
              transition: 'background 0.2s, transform 0.15s, box-shadow 0.2s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#1D4ED8';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(59,130,246,0.35)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = '#3B82F6';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Start Free Trial
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen(v => !v)}
          aria-expanded={open}
          aria-label={open ? 'Close menu' : 'Open menu'}
          className="hdr-mobile-btn"
          style={{
            display: 'none',
            marginLeft: 'auto',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 8,
            color: '#0F172A',
          }}
        >
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {open ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div style={{
          borderTop: '1px solid rgba(15,23,42,0.08)',
          background: 'rgba(255,255,255,0.99)',
          padding: '12px 24px 20px',
        }}>
          {NAV_LINKS.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              onClick={() => setOpen(false)}
              style={{
                display: 'block',
                padding: '12px 0',
                borderBottom: '1px solid rgba(15,23,42,0.06)',
                color: 'rgba(15,23,42,0.7)',
                textDecoration: 'none',
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {label}
            </Link>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            <a
              href="https://app.ifueltax.com/#/login"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block', textAlign: 'center',
                padding: '11px 20px', borderRadius: 8,
                border: '1.5px solid #E2E8F0',
                color: '#334155', textDecoration: 'none',
                fontSize: 14, fontWeight: 600,
              }}
            >
              Login
            </a>
            <a
              href="https://app.ifueltax.com/#/login?action_type=register"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block', textAlign: 'center',
                padding: '11px 20px', borderRadius: 8,
                background: '#3B82F6', color: '#fff',
                textDecoration: 'none', fontSize: 14, fontWeight: 700,
              }}
            >
              Start Free Trial
            </a>
          </div>
        </div>
      )}

      {/* Responsive styles */}
      <style>{`
        @media (max-width: 767px) {
          .hdr-desktop-nav { display: none !important; }
          .hdr-mobile-btn { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}
